<template>
  <div class="debt-view-container">
    <MainHeader :title="'Debt Payoff'">
      <template #left>
        <font-awesome-icon
          @click="router.back()"
          :icon="['fas', 'arrow-left-long']"
          class="back-arrow pointer"
          role="button"
          aria-label="Go back"
        />
      </template>
    </MainHeader>

    <div class="view content content-padding">
      <div class="intro-card">
        <div class="intro-icon">
          <font-awesome-icon :icon="['fas', 'credit-card']" />
        </div>
        <p class="intro-text">
          Add your debts, pick a strategy, and see exactly when you'll be
          debt-free and how much interest you'll save.
        </p>
      </div>

      <div class="ud-section">
        <div class="section-header-row">
          <span class="section-label">Your Debts</span>
          <button
            v-if="debts.length > 0 && !addingDebt"
            type="button"
            class="link-btn mt-2"
            @click="startAddDebt"
          >
            + Add another
          </button>
        </div>

        <div
          v-if="debts.length === 0 && !addingDebt"
          class="wrapper-card empty-debts"
        >
          <font-awesome-icon
            :icon="['fas', 'sack-dollar']"
            class="empty-icon"
          />
          <p class="empty-title">No debts added yet</p>
          <p class="empty-sub">
            Add credit cards, loans, or any other debt you want to pay off.
          </p>
          <button type="button" class="gradient-button" @click="startAddDebt">
            + Add your first debt
          </button>
        </div>

        <div
          v-for="(debt, idx) in debts"
          :key="debt.id"
          class="wrapper-card debt-card"
        >
          <div class="debt-card-header">
            <div class="debt-icon-wrap" :style="iconStyle(debt.color)">
              <font-awesome-icon :icon="['fas', debt.icon || 'credit-card']" />
            </div>
            <div class="debt-info">
              <p class="debt-name">{{ debt.name }}</p>
              <p class="debt-meta">
                {{ debt.rate.toFixed(2) }}% · ${{
                  formatNumber(debt.minPayment)
                }}/mo
              </p>
            </div>
            <div class="debt-actions">
              <button
                type="button"
                class="icon-btn"
                @click="editDebt(idx)"
                aria-label="Edit"
              >
                <font-awesome-icon :icon="['fas', 'pen']" />
              </button>
              <button
                type="button"
                class="icon-btn icon-btn-danger"
                @click="removeDebt(idx)"
                aria-label="Remove"
              >
                <font-awesome-icon :icon="['fas', 'trash']" />
              </button>
            </div>
          </div>
          <div class="debt-balance-row">
            <span class="debt-balance-label">Balance</span>
            <span class="debt-balance-value">
              ${{ formatNumber(debt.balance) }}
            </span>
          </div>
        </div>

        <div v-if="addingDebt" class="wrapper-card add-debt-card">
          <div class="add-debt-header">
            <span class="add-debt-title">
              {{ editingIndex !== null ? 'Edit Debt' : 'Add a Debt' }}
            </span>
            <button
              type="button"
              class="icon-btn"
              @click="cancelAddDebt"
              aria-label="Cancel"
            >
              <font-awesome-icon :icon="['fas', 'xmark']" />
            </button>
          </div>

          <div v-if="editingIndex === null" class="mode-toggle small">
            <button
              type="button"
              class="mode-btn"
              :class="{ active: addSource === 'manual' }"
              @click="setAddSource('manual')"
            >
              Manual
            </button>
            <button
              type="button"
              class="mode-btn"
              :class="{ active: addSource === 'account' }"
              @click="setAddSource('account')"
              :disabled="loadingAccounts"
            >
              From accounts
            </button>
          </div>

          <div
            v-if="editingIndex === null && addSource === 'account'"
            class="ud-field mt"
          >
            <label class="ud-label">Choose an account</label>
            <div v-if="loadingAccounts" class="loading-text">
              Loading your accounts…
            </div>
            <div
              v-else-if="liabilityAccounts.length === 0"
              class="loading-text"
            >
              No connected debt accounts found. Switch to "Manual" to add one.
            </div>
            <div v-else class="account-list">
              <button
                v-for="acct in liabilityAccounts"
                :key="acct.id || acct.accountId"
                type="button"
                class="account-option"
                @click="prefillFromAccount(acct)"
              >
                <font-awesome-icon
                  :icon="['fas', 'building-columns']"
                  class="account-icon"
                />
                <div class="account-text">
                  <span class="account-name">{{ acct.name }}</span>
                  <span
                    v-if="acct.currentBalance != null"
                    class="account-balance"
                  >
                    ${{ formatNumber(Math.abs(acct.currentBalance)) }}
                  </span>
                </div>
              </button>
            </div>
          </div>

          <div class="ud-field mt">
            <label class="ud-label">Debt name</label>
            <input
              v-model="form.name"
              type="text"
              class="input"
              placeholder="e.g. Chase Visa, Car loan"
            />
          </div>

          <div class="ud-field mt">
            <label class="ud-label">Current balance</label>
            <div class="currency-input">
              <span class="currency-symbol">$</span>
              <input
                v-model.number="form.balance"
                type="number"
                class="input currency-field"
                placeholder="0"
                inputmode="decimal"
                min="0"
              />
            </div>
          </div>

          <div class="form-row mt">
            <div class="ud-field flex-1">
              <label class="ud-label">Interest rate (APR)</label>
              <div class="percent-input">
                <input
                  v-model.number="form.rate"
                  type="number"
                  class="input percent-field"
                  placeholder="0"
                  inputmode="decimal"
                  step="0.01"
                  min="0"
                />
                <span class="percent-symbol">%</span>
              </div>
            </div>

            <div class="ud-field flex-1">
              <label class="ud-label">Min monthly payment</label>
              <div class="currency-input">
                <span class="currency-symbol">$</span>
                <input
                  v-model.number="form.minPayment"
                  type="number"
                  class="input currency-field"
                  placeholder="0"
                  inputmode="decimal"
                  min="0"
                />
              </div>
            </div>
          </div>

          <div v-if="formError" class="form-error">
            <font-awesome-icon :icon="['fas', 'circle-exclamation']" />
            <span>{{ formError }}</span>
          </div>
          <div v-if="formWarning" class="form-warn">
            <font-awesome-icon :icon="['fas', 'triangle-exclamation']" />
            <span>{{ formWarning }}</span>
          </div>

          <button
            type="button"
            class="gradient-button w-100 mt-3"
            :disabled="!isFormValid"
            @click="saveDebt"
          >
            {{ editingIndex !== null ? 'Save Changes' : 'Add Debt' }}
          </button>
        </div>
      </div>

      <div v-if="debts.length > 0" class="ud-section">
        <span class="section-label">Payment Plan</span>

        <div class="wrapper-card">
          <div class="ud-field">
            <label class="ud-label">Total monthly payment toward debt</label>
            <div class="currency-input">
              <span class="currency-symbol">$</span>
              <input
                v-model.number="monthlyBudget"
                type="number"
                class="input currency-field"
                :placeholder="String(totalMinPayments)"
                inputmode="decimal"
                :min="totalMinPayments"
              />
            </div>
            <div class="payment-meta">
              <span class="meta-item">
                Minimums:
                <strong>${{ formatNumber(totalMinPayments) }}</strong>
              </span>
              <span v-if="extraAmount > 0" class="meta-item meta-positive">
                Extra: <strong>${{ formatNumber(extraAmount) }}</strong>
              </span>
            </div>
            <p
              v-if="extraAmount === 0 && debts.length > 1"
              class="payment-hint"
            >
              <font-awesome-icon :icon="['fas', 'lightbulb']" />
              Strategies only differ when you pay more than the minimums. Try
              adding even $50 extra to see how each strategy compares.
            </p>
          </div>

          <div v-if="paymentValidation" class="form-error mt">
            <font-awesome-icon :icon="['fas', 'circle-exclamation']" />
            <span>{{ paymentValidation }}</span>
          </div>

          <div class="ud-field mt" ref="budgetDropdownRef">
            <label class="ud-label">
              Pull extra payment from a budget category
              <span class="optional-tag">optional</span>
            </label>
            <button
              type="button"
              class="form-select grey-outline-form-select category-trigger"
              :class="{ open: budgetDropdownOpen }"
              @click="budgetDropdownOpen = !budgetDropdownOpen"
              :disabled="loadingBudget"
            >
              <span
                class="category-trigger-value"
                :class="{ placeholder: !selectedBudgetNode }"
              >
                {{ budgetTriggerLabel }}
              </span>
              <font-awesome-icon
                :icon="['fas', 'chevron-down']"
                class="category-chevron"
                :class="{ open: budgetDropdownOpen }"
              />
            </button>

            <transition name="sub-slide">
              <div v-if="budgetDropdownOpen" class="category-menu">
                <button
                  type="button"
                  class="category-option"
                  :class="{ selected: !selectedBudgetNode }"
                  @click="selectBudgetNone"
                >
                  <span class="option-name">— None / not budgeted —</span>
                </button>
                <template
                  v-for="cat in spendingCategories"
                  :key="cat.categoryID"
                >
                  <button
                    type="button"
                    class="category-option category-parent"
                    :class="{
                      selected:
                        selectedBudgetNode &&
                        selectedBudgetNode.type === 'category' &&
                        selectedBudgetNode.id === cat.categoryID,
                    }"
                    @click="selectBudgetCategory(cat)"
                  >
                    <span class="option-name">{{ cat.name }}</span>
                    <span
                      v-if="parentDisplayAvailable(cat) !== null"
                      class="option-remaining"
                    >
                      ${{ formatNumber(parentDisplayAvailable(cat)) }} left
                    </span>
                  </button>
                  <button
                    v-for="sub in cat.subcategories || []"
                    :key="sub.subcategoryID"
                    type="button"
                    class="category-option category-sub"
                    :class="{
                      selected:
                        selectedBudgetNode &&
                        selectedBudgetNode.type === 'subcategory' &&
                        selectedBudgetNode.id === sub.subcategoryID,
                    }"
                    @click="selectBudgetSubcategory(cat, sub)"
                  >
                    <span class="sub-indent">↳</span>
                    <span class="option-name">{{ sub.name }}</span>
                    <span class="option-remaining">
                      ${{ formatNumber(sub.available) }} left
                    </span>
                  </button>
                </template>
                <div
                  v-if="!loadingBudget && spendingCategories.length === 0"
                  class="category-empty"
                >
                  No budget categories yet for this month.
                </div>
              </div>
            </transition>

            <div v-if="budgetWarning" class="form-warn mt">
              <font-awesome-icon :icon="['fas', 'triangle-exclamation']" />
              <span>{{ budgetWarning }}</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="canRunSimulation" class="ud-section">
        <span class="section-label">Strategy</span>

        <div class="strategy-grid">
          <button
            v-for="strat in strategyOptions"
            :key="strat.id"
            type="button"
            class="strategy-card"
            :class="{ selected: selectedStrategy === strat.id }"
            @click="selectedStrategy = strat.id"
          >
            <div class="strategy-icon-wrap" :class="strat.tone">
              <font-awesome-icon :icon="['fas', strat.icon]" />
            </div>
            <p class="strategy-name">{{ strat.name }}</p>
            <p class="strategy-desc">{{ strat.desc }}</p>
            <div v-if="strategyResults[strat.id]" class="strategy-stats">
              <span
                v-if="strategyResults[strat.id].neverPaysOff"
                class="strategy-stat strategy-stat-warn"
              >
                Won't clear
              </span>
              <template v-else>
                <span class="strategy-stat">
                  {{ strategyResults[strat.id].monthsToPayoff }} mo
                </span>
                <span class="strategy-stat">
                  ${{ formatNumber(strategyResults[strat.id].totalInterest) }}
                  int
                </span>
              </template>
            </div>
          </button>
        </div>

        <div
          v-if="selectedStrategy === 'custom'"
          class="wrapper-card mt-3 custom-order-card"
        >
          <p class="custom-order-title">Reorder your debts</p>
          <p class="custom-order-sub">
            Topmost debt gets the extra payment first. Use the arrows to
            reorder.
          </p>
          <div
            v-for="(debt, idx) in customOrder"
            :key="debt.id"
            class="custom-order-row"
          >
            <span class="custom-order-num">{{ idx + 1 }}</span>
            <span class="custom-order-name">{{ debt.name }}</span>
            <span class="custom-order-bal">
              ${{ formatNumber(debt.balance) }}
            </span>
            <div class="reorder-buttons">
              <button
                type="button"
                class="icon-btn"
                :disabled="idx === 0"
                @click="moveCustom(idx, -1)"
                aria-label="Move up"
              >
                <font-awesome-icon :icon="['fas', 'chevron-up']" />
              </button>
              <button
                type="button"
                class="icon-btn"
                :disabled="idx === customOrder.length - 1"
                @click="moveCustom(idx, 1)"
                aria-label="Move down"
              >
                <font-awesome-icon :icon="['fas', 'chevron-down']" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="canRunSimulation && currentResult" class="ud-section">
        <span class="section-label">Your Plan</span>

        <div v-if="currentResult.neverPaysOff" class="unpayable-warning mb-2">
          <div class="unpayable-icon">
            <font-awesome-icon :icon="['fas', 'triangle-exclamation']" />
          </div>
          <div class="unpayable-text">
            <p class="unpayable-title">
              This payment plan won't clear your debt
            </p>
            <p class="unpayable-body">
              <span v-if="currentResult.unpaidDebts.length === debts.length">
                At your current monthly amount, the interest is outpacing your
                payments. Your balance is growing.
              </span>
              <span v-else>
                {{ currentResult.unpaidDebts.length }} of your
                {{ debts.length }} debts won't be paid off:
                <strong>
                  {{ currentResult.unpaidDebts.map((d) => d.name).join(', ') }}
                </strong>
                .
              </span>
              Try increasing your total monthly payment, or consider refinancing
              high-rate debts.
            </p>
          </div>
        </div>

        <div class="results-card">
          <div class="result-stat-grid">
            <div class="result-stat">
              <span class="result-stat-label">Debt-free in</span>
              <span v-if="currentResult.neverPaysOff" class="result-stat-value">
                Never
              </span>
              <span v-else class="result-stat-value">
                {{ currentResult.monthsToPayoff }} months
              </span>
              <span v-if="!currentResult.neverPaysOff" class="result-stat-sub">
                {{ payoffDateLabel }}
              </span>
              <span v-else class="result-stat-sub">
                at this payment level
              </span>
            </div>
            <div class="result-stat">
              <span class="result-stat-label">Total interest</span>
              <span class="result-stat-value">
                ${{ formatNumber(currentResult.totalInterest) }}
              </span>
            </div>
            <div
              v-if="interestSaved > 0"
              class="result-stat result-stat-savings"
            >
              <span class="result-stat-label">Saved vs. minimums</span>
              <span class="result-stat-value">
                ${{ formatNumber(interestSaved) }}
              </span>
              <span class="result-stat-sub">
                {{ monthsSaved }} months earlier
              </span>
            </div>
          </div>
        </div>

        <div class="wrapper-card mt milestones-card">
          <button
            type="button"
            class="expandable-header"
            @click="showMilestones = !showMilestones"
          >
            <span class="expandable-title">Per-debt payoff dates</span>
            <font-awesome-icon
              :icon="['fas', 'chevron-down']"
              class="expandable-chevron"
              :class="{ open: showMilestones }"
            />
          </button>
          <transition name="sub-slide">
            <div v-if="showMilestones" class="milestones-body">
              <div
                v-for="m in currentResult.milestones"
                :key="m.id"
                class="milestone-row"
              >
                <div class="milestone-info">
                  <span class="milestone-name">{{ m.name }}</span>
                  <span class="milestone-meta">
                    Paid off in {{ m.month }} mo · ${{
                      formatNumber(m.totalInterest)
                    }}
                    interest
                  </span>
                </div>
                <span class="milestone-date">
                  {{ monthOffsetLabel(m.month) }}
                </span>
              </div>
              <div
                v-for="d in currentResult.unpaidDebts"
                :key="'unpaid-' + d.id"
                class="milestone-row milestone-row-unpaid"
              >
                <div class="milestone-info">
                  <span class="milestone-name">{{ d.name }}</span>
                  <span class="milestone-meta">
                    Not paid off · final balance ${{
                      formatNumber(d.finalBalance)
                    }}
                  </span>
                </div>
                <span class="milestone-date milestone-date-unpaid">
                  Won't clear
                </span>
              </div>
            </div>
          </transition>
        </div>

        <div class="wrapper-card mt-1 milestones-card">
          <button
            type="button"
            class="expandable-header"
            @click="showSchedule = !showSchedule"
          >
            <span class="expandable-title">Month-by-month schedule</span>
            <font-awesome-icon
              :icon="['fas', 'chevron-down']"
              class="expandable-chevron"
              :class="{ open: showSchedule }"
            />
          </button>
          <transition name="sub-slide">
            <div v-if="showSchedule" class="schedule-body">
              <div class="schedule-table-wrap">
                <table class="schedule-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th class="text-right">Paid</th>
                      <th class="text-right">Interest</th>
                      <th class="text-right">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in currentResult.schedule" :key="row.month">
                      <td>{{ row.month }}</td>
                      <td class="text-right">${{ formatNumber(row.paid) }}</td>
                      <td class="text-right">
                        ${{ formatNumber(row.interest) }}
                      </td>
                      <td class="text-right">
                        ${{ formatNumber(row.balance) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </transition>
        </div>

        <div v-if="selectedStrategy !== 'compare'" class="mt">
          <button
            type="button"
            class="outline-button w-100 mt-3"
            @click="selectedStrategy = 'compare'"
          >
            Compare all strategies →
          </button>
        </div>

        <div v-if="selectedStrategy === 'compare'" class="wrapper-card mt">
          <p class="compare-title">All strategies side-by-side</p>
          <div class="compare-grid">
            <div
              v-for="strat in comparableStrategies"
              :key="strat.id"
              class="compare-row"
              :class="{ best: strat.id === bestStrategyId }"
            >
              <div class="compare-name">
                {{ strat.name }}
                <span v-if="strat.id === bestStrategyId" class="best-tag">
                  Lowest interest
                </span>
              </div>
              <div class="compare-stats">
                <template v-if="strategyResults[strat.id].neverPaysOff">
                  <span class="compare-stat-warn">Won't clear</span>
                </template>
                <template v-else>
                  <span>
                    {{ strategyResults[strat.id].monthsToPayoff }} mo
                  </span>
                  <span>
                    ${{ formatNumber(strategyResults[strat.id].totalInterest) }}
                  </span>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useMyStore } from '../../store';
import MainHeader from '../../components/View-Header.vue';

export default {
  components: { MainHeader },
  setup() {
    const router = useRouter();
    const store = useMyStore();

    const debts = ref([]);
    const nextId = ref(1);
    const debtSnapshots = ref({});

    const addingDebt = ref(false);
    const editingIndex = ref(null);
    const addSource = ref('manual');
    const form = ref({
      name: '',
      balance: 0,
      rate: 0,
      minPayment: 0,
      icon: 'credit-card',
      color: '#7F77DD',
    });
    const formError = ref('');

    const startAddDebt = () => {
      addingDebt.value = true;
      editingIndex.value = null;
      addSource.value = 'manual';
      form.value = {
        name: '',
        balance: 0,
        rate: 0,
        minPayment: 0,
        icon: 'credit-card',
        color: '#7F77DD',
      };
      formError.value = '';
    };

    const cancelAddDebt = () => {
      addingDebt.value = false;
      editingIndex.value = null;
      formError.value = '';
    };

    const editDebt = (idx) => {
      const d = debts.value[idx];
      addingDebt.value = true;
      editingIndex.value = idx;
      form.value = { ...d };
      formError.value = '';
    };

    const removeDebt = (idx) => {
      const removedId = debts.value[idx].id;
      debts.value.splice(idx, 1);
      customOrder.value = customOrder.value.filter((d) =>
        debts.value.find((x) => x.id === d.id)
      );
      delete debtSnapshots.value[removedId];
    };

    const isFormValid = computed(() => {
      const f = form.value;
      return (
        f.name.trim() !== '' && f.balance > 0 && f.rate >= 0 && f.minPayment > 0
      );
    });

    const formWarning = computed(() => {
      const f = form.value;
      if (!(f.balance > 0) || !(f.minPayment > 0) || f.rate <= 0) return '';
      const monthlyInterest = (f.balance * (f.rate / 100)) / 12;
      if (f.minPayment <= monthlyInterest) {
        return `Heads up: at ${f.rate}% APR, monthly interest is $${monthlyInterest.toFixed(
          2
        )}. The minimum payment of $${f.minPayment.toFixed(
          2
        )} won't cover it — your balance will grow over time. (This is common with some payday and predatory loans.) You can still add this debt; the calculator will show you how to escape it.`;
      }
      return '';
    });

    const validateForm = () => {
      const f = form.value;
      if (!f.name.trim()) return 'Give your debt a name.';
      if (!(f.balance > 0)) return 'Balance must be greater than 0.';
      if (f.rate < 0) return 'Interest rate cannot be negative.';
      if (!(f.minPayment > 0)) return 'Minimum payment must be greater than 0.';
      return '';
    };

    const saveDebt = () => {
      const err = validateForm();
      if (err) {
        formError.value = err;
        return;
      }
      const f = form.value;
      if (editingIndex.value !== null) {
        const existingId = debts.value[editingIndex.value].id;
        debts.value[editingIndex.value] = {
          ...debts.value[editingIndex.value],
          ...f,
        };
        if (!(existingId in debtSnapshots.value)) {
          debtSnapshots.value[existingId] = f.balance;
        }
      } else {
        const newDebt = { ...f, id: nextId.value++ };
        debts.value.push(newDebt);
        customOrder.value.push(newDebt);
        debtSnapshots.value[newDebt.id] = newDebt.balance;
      }
      addingDebt.value = false;
      editingIndex.value = null;
      formError.value = '';
    };

    const accounts = ref([]);
    const loadingAccounts = ref(false);

    const fetchAccounts = async () => {
      loadingAccounts.value = true;
      try {
        const response = await store.getPlaidAccountsList();
        accounts.value = response?.data || store.accountsList || [];
      } catch (e) {
        console.error('Failed to load accounts for debt picker', e);
        accounts.value = [];
      } finally {
        loadingAccounts.value = false;
      }
    };

    const liabilityAccounts = computed(() =>
      accounts.value.filter((a) => {
        const t = (a.type || a.accountType || '').toLowerCase();
        return t === 'credit' || t === 'loan' || t === 'liability';
      })
    );

    const setAddSource = (src) => {
      addSource.value = src;
      if (src === 'account' && accounts.value.length === 0) {
        fetchAccounts();
      }
    };

    const prefillFromAccount = (acct) => {
      form.value = {
        ...form.value,
        name: acct.name || 'Account',
        balance: Math.abs(acct.currentBalance || 0),
      };
      addSource.value = 'manual';
    };

    const monthlyBudget = ref(0);
    const userEditedBudget = ref(false);

    const totalMinPayments = computed(() =>
      debts.value.reduce((sum, d) => sum + (d.minPayment || 0), 0)
    );

    const extraAmount = computed(() =>
      Math.max(0, monthlyBudget.value - totalMinPayments.value)
    );

    const paymentValidation = computed(() => {
      if (debts.value.length === 0) return '';
      if (monthlyBudget.value === 0) return '';
      if (monthlyBudget.value < totalMinPayments.value) {
        return `Total monthly must be at least $${totalMinPayments.value.toFixed(
          0
        )} to cover all minimums.`;
      }
      return '';
    });

    watch(totalMinPayments, (newVal) => {
      if (!userEditedBudget.value || monthlyBudget.value < newVal) {
        monthlyBudget.value = newVal;
      }
    });

    watch(monthlyBudget, (newVal, oldVal) => {
      if (newVal !== totalMinPayments.value && oldVal !== 0) {
        userEditedBudget.value = true;
      }
    });

    const categoryBudgets = ref([]);
    const loadingBudget = ref(false);
    const selectedBudgetNode = ref(null);
    const budgetDropdownOpen = ref(false);
    const budgetDropdownRef = ref(null);

    const fetchMonthlyBudget = async () => {
      loadingBudget.value = true;
      try {
        const today = new Date();
        const month = today.getMonth() + 1;
        const year = today.getFullYear();
        const response = await store.getMonthlyBudgetDataForAllCategories(
          month,
          year
        );
        categoryBudgets.value = response?.data?.categoryBudgets || [];
      } catch (e) {
        console.error('Failed to load monthly budget', e);
      } finally {
        loadingBudget.value = false;
      }
    };

    const spendingCategories = computed(() =>
      categoryBudgets.value
        .filter((c) => !c.income)
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
    );

    const parentDisplayAvailable = (cat) => {
      if (cat.subcategories && cat.subcategories.length > 0) {
        return cat.incSubAvailable;
      }
      return cat.available;
    };

    const findBudgetSubcategory = (id) => {
      for (const cat of categoryBudgets.value) {
        const sub = (cat.subcategories || []).find(
          (s) => s.subcategoryID === id
        );
        if (sub) return { sub, parent: cat };
      }
      return null;
    };

    const selectedBudgetCategory = computed(() => {
      if (!selectedBudgetNode.value) return null;
      if (selectedBudgetNode.value.type === 'category') {
        return categoryBudgets.value.find(
          (c) => c.categoryID === selectedBudgetNode.value.id
        );
      }
      const f = findBudgetSubcategory(selectedBudgetNode.value.id);
      return f ? f.parent : null;
    });

    const selectedBudgetSubcategory = computed(() => {
      if (
        !selectedBudgetNode.value ||
        selectedBudgetNode.value.type !== 'subcategory'
      ) {
        return null;
      }
      const f = findBudgetSubcategory(selectedBudgetNode.value.id);
      return f ? f.sub : null;
    });

    const budgetTriggerLabel = computed(() => {
      if (loadingBudget.value && !selectedBudgetNode.value) {
        return 'Loading your budget…';
      }
      if (!selectedBudgetNode.value) return '— None / not budgeted —';
      if (selectedBudgetSubcategory.value) {
        return `${selectedBudgetCategory.value.name} › ${selectedBudgetSubcategory.value.name}`;
      }
      return selectedBudgetCategory.value?.name || '—';
    });

    const selectBudgetNone = () => {
      selectedBudgetNode.value = null;
      budgetDropdownOpen.value = false;
    };
    const selectBudgetCategory = (cat) => {
      selectedBudgetNode.value = { type: 'category', id: cat.categoryID };
      budgetDropdownOpen.value = false;
    };
    const selectBudgetSubcategory = (cat, sub) => {
      selectedBudgetNode.value = {
        type: 'subcategory',
        id: sub.subcategoryID,
      };
      budgetDropdownOpen.value = false;
    };

    const budgetAvailable = computed(() => {
      if (selectedBudgetSubcategory.value) {
        return selectedBudgetSubcategory.value.available;
      }
      if (selectedBudgetCategory.value) {
        return parentDisplayAvailable(selectedBudgetCategory.value);
      }
      return null;
    });

    const budgetWarning = computed(() => {
      if (budgetAvailable.value === null) return '';
      if (extraAmount.value === 0) return '';
      if (extraAmount.value > budgetAvailable.value) {
        const diff = extraAmount.value - budgetAvailable.value;
        return `Your extra payment of $${formatNumber(
          extraAmount.value
        )} exceeds the $${formatNumber(
          budgetAvailable.value
        )} available in this category by $${formatNumber(diff)}.`;
      }
      return '';
    });

    const handleClickOutside = (e) => {
      if (
        budgetDropdownRef.value &&
        !budgetDropdownRef.value.contains(e.target)
      ) {
        budgetDropdownOpen.value = false;
      }
    };

    const strategyOptions = [
      {
        id: 'avalanche',
        name: 'Avalanche',
        desc: 'Highest interest rate first. Saves the most money.',
        icon: 'mountain',
        tone: 'purple',
      },
      {
        id: 'snowball',
        name: 'Snowball',
        desc: 'Smallest balance first. Quick psychological wins.',
        icon: 'snowflake',
        tone: 'blue',
      },
      {
        id: 'highestPayment',
        name: 'Highest payment',
        desc: 'Biggest minimum first. Frees up cash flow fastest.',
        icon: 'arrow-trend-up',
        tone: 'teal',
      },
      {
        id: 'custom',
        name: 'Custom',
        desc: 'Choose your own order.',
        icon: 'hand',
        tone: 'coral',
      },
    ];

    const selectedStrategy = ref('avalanche');
    const customOrder = ref([]);

    const moveCustom = (idx, dir) => {
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= customOrder.value.length) return;
      const arr = customOrder.value;
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      customOrder.value = [...arr];
    };

    const orderDebtsByStrategy = (strategyId) => {
      const list = debts.value.map((d) => ({ ...d }));
      switch (strategyId) {
        case 'avalanche':
          return list.sort((a, b) => b.rate - a.rate);
        case 'snowball':
          return list.sort((a, b) => a.balance - b.balance);
        case 'highestPayment':
          return list.sort((a, b) => b.minPayment - a.minPayment);
        case 'custom':
          return customOrder.value
            .map((c) => debts.value.find((d) => d.id === c.id))
            .filter(Boolean)
            .map((d) => ({ ...d }));
        default:
          return list;
      }
    };

    const simulate = (strategyId, monthlyBudgetAmount) => {
      const ordered = orderDebtsByStrategy(strategyId);
      if (ordered.length === 0) return null;

      const state = ordered.map((d) => ({
        id: d.id,
        name: d.name,
        balance: d.balance,
        rate: d.rate,
        minPayment: d.minPayment,
        paidOffMonth: null,
        totalInterest: 0,
      }));

      const schedule = [];
      let month = 0;
      let totalInterest = 0;
      let totalPaid = 0;
      const MAX_MONTHS = 600;
      let balanceAtMonth6 = 0;

      while (state.some((d) => d.balance > 0.01) && month < MAX_MONTHS) {
        month++;
        let monthInterest = 0;
        let monthPaid = 0;

        for (const d of state) {
          if (d.balance <= 0) continue;
          const interest = (d.balance * (d.rate / 100)) / 12;
          d.balance += interest;
          d.totalInterest += interest;
          monthInterest += interest;
        }

        let availableExtra = monthlyBudgetAmount;
        for (const d of state) {
          if (d.balance <= 0) continue;
          const payment = Math.min(d.minPayment, d.balance);
          d.balance -= payment;
          availableExtra -= payment;
          monthPaid += payment;
        }

        if (availableExtra > 0) {
          for (const d of state) {
            if (d.balance <= 0) continue;
            const extra = Math.min(availableExtra, d.balance);
            d.balance -= extra;
            availableExtra -= extra;
            monthPaid += extra;
            if (availableExtra <= 0) break;
          }
        }

        for (const d of state) {
          if (d.balance <= 0.01 && d.paidOffMonth === null) {
            d.balance = 0;
            d.paidOffMonth = month;
          }
        }

        totalInterest += monthInterest;
        totalPaid += monthPaid;

        const remaining = state.reduce((s, d) => s + Math.max(0, d.balance), 0);
        schedule.push({
          month,
          paid: monthPaid,
          interest: monthInterest,
          balance: remaining,
        });

        if (month === 6) {
          balanceAtMonth6 = remaining;
        }
      }

      const milestones = state
        .filter((d) => d.paidOffMonth !== null)
        .sort((a, b) => a.paidOffMonth - b.paidOffMonth)
        .map((d) => ({
          id: d.id,
          name: d.name,
          month: d.paidOffMonth,
          totalInterest: d.totalInterest,
        }));

      const unpaidDebts = state
        .filter((d) => d.paidOffMonth === null)
        .map((d) => ({
          id: d.id,
          name: d.name,
          finalBalance: d.balance,
          totalInterest: d.totalInterest,
          grewBy: d.balance - (debtSnapshots.value[d.id] ?? d.balance),
        }));

      let neverPaysOff = unpaidDebts.length > 0;

      if (!neverPaysOff && month >= 12 && balanceAtMonth6 > 0) {
        const balanceAtMonth12 = schedule[11]?.balance ?? 0;
        if (balanceAtMonth12 >= balanceAtMonth6) {
          neverPaysOff = true;
        }
      }

      return {
        monthsToPayoff: month,
        totalInterest,
        totalPaid,
        milestones,
        schedule,
        neverPaysOff,
        unpaidDebts,
      };
    };

    const canRunSimulation = computed(
      () =>
        debts.value.length > 0 &&
        monthlyBudget.value >= totalMinPayments.value &&
        totalMinPayments.value > 0
    );

    const strategyResults = computed(() => {
      if (!canRunSimulation.value) return {};
      const ids = ['avalanche', 'snowball', 'highestPayment', 'custom'];
      const out = {};
      for (const id of ids) {
        out[id] = simulate(id, monthlyBudget.value);
      }
      return out;
    });

    const currentResult = computed(() => {
      if (selectedStrategy.value === 'compare') {
        return strategyResults.value['avalanche'];
      }
      return strategyResults.value[selectedStrategy.value] || null;
    });

    const minimumOnlyResult = computed(() => {
      if (!canRunSimulation.value) return null;
      return simulate('avalanche', totalMinPayments.value);
    });

    const interestSaved = computed(() => {
      if (!currentResult.value || !minimumOnlyResult.value) return 0;
      if (
        currentResult.value.neverPaysOff ||
        minimumOnlyResult.value.neverPaysOff
      )
        return 0;
      return Math.max(
        0,
        minimumOnlyResult.value.totalInterest -
          currentResult.value.totalInterest
      );
    });

    const monthsSaved = computed(() => {
      if (!currentResult.value || !minimumOnlyResult.value) return 0;
      if (
        currentResult.value.neverPaysOff ||
        minimumOnlyResult.value.neverPaysOff
      )
        return 0;
      return Math.max(
        0,
        minimumOnlyResult.value.monthsToPayoff -
          currentResult.value.monthsToPayoff
      );
    });

    const monthOffsetLabel = (monthsAhead) => {
      const d = new Date();
      d.setMonth(d.getMonth() + monthsAhead);
      return d.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
      });
    };

    const payoffDateLabel = computed(() => {
      if (!currentResult.value) return '';
      return monthOffsetLabel(currentResult.value.monthsToPayoff);
    });

    const comparableStrategies = computed(() =>
      strategyOptions.filter((s) => strategyResults.value[s.id])
    );

    const bestStrategyId = computed(() => {
      const entries = Object.entries(strategyResults.value).filter(
        ([, r]) => !r.neverPaysOff
      );
      if (entries.length === 0) return null;
      return entries.reduce(
        (best, [id, r]) =>
          !best || r.totalInterest < strategyResults.value[best].totalInterest
            ? id
            : best,
        null
      );
    });

    const showMilestones = ref(false);
    const showSchedule = ref(false);

    onMounted(() => {
      document.addEventListener('click', handleClickOutside);
      fetchMonthlyBudget();
    });

    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside);
    });

    const formatNumber = (n) => {
      if (n == null || isNaN(n)) return '0';
      return Number(n).toLocaleString('en-US', {
        maximumFractionDigits: 0,
      });
    };

    const iconStyle = (color) => ({
      backgroundColor: color ? `${color}20` : '#f4f4f6',
      color: color || '#888',
    });

    return {
      router,
      debts,
      addingDebt,
      editingIndex,
      addSource,
      form,
      formError,
      formWarning,
      isFormValid,
      startAddDebt,
      cancelAddDebt,
      editDebt,
      removeDebt,
      saveDebt,
      loadingAccounts,
      liabilityAccounts,
      setAddSource,
      prefillFromAccount,
      monthlyBudget,
      totalMinPayments,
      extraAmount,
      paymentValidation,
      loadingBudget,
      spendingCategories,
      parentDisplayAvailable,
      selectedBudgetNode,
      budgetDropdownOpen,
      budgetDropdownRef,
      budgetTriggerLabel,
      selectBudgetNone,
      selectBudgetCategory,
      selectBudgetSubcategory,
      budgetWarning,
      strategyOptions,
      selectedStrategy,
      customOrder,
      moveCustom,
      strategyResults,
      currentResult,
      canRunSimulation,
      interestSaved,
      monthsSaved,
      payoffDateLabel,
      monthOffsetLabel,
      comparableStrategies,
      bestStrategyId,
      showMilestones,
      showSchedule,
      formatNumber,
      iconStyle,
    };
  },
};
</script>

<style scoped>
.debt-view-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.back-arrow {
  font-size: 18px;
  color: var(--color-primary);
  cursor: pointer;
  transition: transform var(--transition-fast);
}
.back-arrow:hover {
  transform: translateX(-2px);
}
.dark-theme .back-arrow {
  color: var(--color-accent-teal);
}

.intro-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--purple-50);
  border-radius: var(--radius-lg);
  padding: 0.85rem 1rem;
  margin-bottom: 1.25rem;
  border: 1px solid #ece9fb;
}
.dark-theme .intro-card {
  background: #1e1a40;
  border-color: #2a2060;
}
.intro-icon {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-sm);
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-accent-purple);
  font-size: 14px;
  flex-shrink: 0;
}
.dark-theme .intro-icon {
  background: #2a2060;
}
.intro-text {
  font-size: 0.8rem;
  color: #213547;
  margin: 0;
  line-height: 1.4;
}
.dark-theme .intro-text {
  color: #d4d2d2;
}

.section-header-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.5rem;
}
.link-btn {
  background: none;
  border: none;
  padding: 0;
  color: var(--color-accent-purple);
  font-family: 'Poppins', sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
}
.link-btn:hover {
  color: var(--purple-600);
}

.empty-debts {
  text-align: center;
  padding: 2rem 1.5rem !important;
}
.empty-icon {
  font-size: 36px;
  color: var(--color-accent-purple);
  margin-bottom: 1rem;
}
.empty-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--color-primary);
  margin: 0 0 0.4rem;
}
.dark-theme .empty-title {
  color: white;
}
.empty-sub {
  font-size: 0.78rem;
  color: #6b7280;
  margin: 0 0 1.25rem;
}
.dark-theme .empty-sub {
  color: #9aa6b8;
}

.debt-card {
  margin-bottom: 0.75rem;
}
.debt-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 0.85rem;
}
.debt-icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
}
.debt-info {
  flex: 1;
  min-width: 0;
}
.debt-name {
  font-size: 0.9rem;
  font-weight: 700;
  color: #213547;
  margin: 0 0 2px;
}
.dark-theme .debt-name {
  color: white;
}
.debt-meta {
  font-size: 0.72rem;
  color: #6b7280;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dark-theme .debt-meta {
  color: #9aa6b8;
}
.debt-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.debt-balance-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding-top: 0.85rem;
  border-top: 1px solid #f0f0f0;
}
.dark-theme .debt-balance-row {
  border-top-color: #2a3a4e;
}
.debt-balance-label {
  font-size: 0.75rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
}
.dark-theme .debt-balance-label {
  color: #9aa6b8;
}
.debt-balance-value {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--color-primary);
}
.dark-theme .debt-balance-value {
  color: white;
}

.icon-btn {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: background var(--transition-fast);
  padding: 0;
}
.icon-btn:hover {
  background: #f4f4f6;
}
.dark-theme .icon-btn {
  color: #9aa6b8;
}
.dark-theme .icon-btn:hover {
  background: #1a2535;
}
.icon-btn-danger:hover {
  background: #fdeee7;
  color: #d85a30;
}
.dark-theme .icon-btn-danger:hover {
  background: #2e1d18;
}
.icon-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.add-debt-card {
  border: 2px solid var(--color-accent-purple) !important;
}
.add-debt-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.85rem;
}
.add-debt-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--color-primary);
}
.dark-theme .add-debt-title {
  color: white;
}

.mode-toggle {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  background: #f4f4f6;
  padding: 4px;
  border-radius: var(--radius-pill);
}
.mode-toggle.small {
  margin-bottom: 0;
}
.dark-theme .mode-toggle {
  background: #1a2535;
}
.mode-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0.5rem 0.75rem;
  background: transparent;
  border: none;
  border-radius: var(--radius-pill);
  font-family: 'Poppins', sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
}
.mode-btn.active {
  background: white;
  color: var(--color-primary);
  box-shadow: var(--shadow-sm);
}
.mode-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.dark-theme .mode-btn {
  color: #9aa6b8;
}
.dark-theme .mode-btn.active {
  background: #212e3e;
  color: white;
}

.loading-text {
  font-size: 0.8rem;
  color: #6b7280;
  text-align: center;
  padding: 1rem;
}
.dark-theme .loading-text {
  color: #9aa6b8;
}
.account-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 200px;
  overflow-y: auto;
}
.account-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0.7rem 0.85rem;
  background: #fafbfc;
  border: 1px solid #e7e7e7;
  border-radius: var(--radius-sm);
  font-family: 'Poppins', sans-serif;
  font-size: 0.85rem;
  color: #213547;
  text-align: left;
  cursor: pointer;
  transition: background var(--transition-fast);
}
.account-option:hover {
  background: #f4f1ff;
  border-color: var(--color-accent-purple);
}
.dark-theme .account-option {
  background: #1a2535;
  border-color: #2a3a4e;
  color: #d4d2d2;
}
.dark-theme .account-option:hover {
  background: #1e1a40;
}
.account-icon {
  color: var(--color-accent-teal);
  font-size: 12px;
}
.account-text {
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}
.account-name {
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.account-balance {
  font-size: 0.78rem;
  color: #6b7280;
  flex-shrink: 0;
}
.dark-theme .account-balance {
  color: #9aa6b8;
}

.form-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  /* grid-template-columns: 1fr 1fr; */
  gap: 1em;
  margin-top: 0.75em;
}
.flex-1 {
  flex: 1;
}

.currency-input,
.percent-input {
  position: relative;
}
.input {
  width: 100%;
}
.currency-symbol {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1em;
  font-weight: 600;
  color: #6b7280;
  pointer-events: none;
  z-index: 1;
}
.percent-symbol {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1em;
  font-weight: 600;
  color: #6b7280;
  pointer-events: none;
  z-index: 1;
}
.dark-theme .currency-symbol,
.dark-theme .percent-symbol {
  color: #9aa6b8;
}
.currency-field {
  padding-left: 1.85rem !important;
}
.percent-field {
  padding-right: 1.85rem !important;
}

.form-error,
.form-warn {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 0.65rem 0.85rem;
  border-radius: var(--radius-sm);
  font-size: 0.78rem;
  line-height: 1.4;
  margin-top: 0.85rem;
}
.form-error {
  background: #fdeee7;
  color: #b03d12;
  border: 1px solid #f5cdb8;
}
.form-warn {
  background: #fff8e6;
  color: #8a6300;
  border: 1px solid #f4e4b8;
}
.dark-theme .form-error {
  background: #2e1d18;
  color: #f5b89a;
  border-color: #4a2a20;
}
.dark-theme .form-warn {
  background: #2e2718;
  color: #e6cf80;
  border-color: #4a3e1f;
}

.payment-meta {
  display: flex;
  gap: 16px;
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: #6b7280;
}
.dark-theme .payment-meta {
  color: #9aa6b8;
}
.meta-positive {
  color: var(--color-accent-green);
}
.dark-theme .meta-positive {
  color: var(--color-accent-teal);
}

.payment-hint {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 0.6rem;
  padding: 0.6rem 0.75rem;
  background: var(--purple-50);
  border-radius: var(--radius-sm);
  font-size: 0.72rem;
  color: #4b5563;
  line-height: 1.4;
}
.payment-hint svg {
  color: var(--color-accent-purple);
  font-size: 11px;
  margin-top: 2px;
  flex-shrink: 0;
}
.dark-theme .payment-hint {
  background: #1e1a40;
  color: #cbd5e1;
}

.optional-tag {
  font-size: 0.65rem;
  font-weight: 500;
  color: #9ca3af;
  text-transform: lowercase;
  letter-spacing: 0;
  margin-left: 4px;
  font-style: italic;
}

.category-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  text-align: left;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  background-image: none !important;
  padding-right: 14px !important;
  font-family: 'Poppins', sans-serif;
  color: #213547;
}
.dark-theme .category-trigger {
  color: #d4d2d2;
}
.category-trigger.open {
  border-color: var(--color-accent-purple) !important;
  box-shadow: 0 0 0 3px rgba(127, 119, 221, 0.1);
}
.category-trigger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.category-trigger-value {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.category-trigger-value.placeholder {
  color: #9ca3af;
}
.category-chevron {
  font-size: 11px;
  color: #6b7280;
  transition: transform var(--transition-fast);
  flex-shrink: 0;
}
.category-chevron.open {
  transform: rotate(180deg);
}

.category-menu {
  margin-top: 6px;
  background: white;
  border: 1.5px solid #e7e7e7;
  border-radius: var(--radius-sm);
  overflow: hidden;
  max-height: 320px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.dark-theme .category-menu {
  background: #212e3e;
  border-color: #2a3a4e;
}
.category-option {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0.6rem 0.85rem;
  background: transparent;
  border: none;
  border-bottom: 1px solid #f4f4f6;
  border-radius: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 0.85rem;
  color: #213547;
  text-align: left;
  cursor: pointer;
}
.category-option:last-child {
  border-bottom: none;
}
.category-option:hover {
  background: #fafafa;
}
.category-option.selected {
  background: var(--purple-50);
  color: var(--color-accent-purple);
  font-weight: 600;
}
.dark-theme .category-option {
  color: #d4d2d2;
  border-bottom-color: #2a3a4e;
}
.dark-theme .category-option:hover {
  background: #1a2535;
}
.dark-theme .category-option.selected {
  background: #1e1a40;
}
.category-parent {
  font-weight: 600;
}
.category-sub {
  padding-left: 1.5rem;
  font-size: 0.82rem;
  font-weight: 400;
  background: #fafbfc;
}
.dark-theme .category-sub {
  background: #1a2535;
}
.sub-indent {
  color: #c0c0c8;
  font-size: 0.75rem;
}
.dark-theme .sub-indent {
  color: #4a5a6e;
}
.option-name {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.option-remaining {
  font-size: 0.7rem;
  color: #6b7280;
  flex-shrink: 0;
  font-weight: 500;
}
.dark-theme .option-remaining {
  color: #9aa6b8;
}
.category-option.selected .option-remaining {
  color: var(--color-accent-purple);
}
.category-empty {
  padding: 1rem;
  text-align: center;
  font-size: 0.8rem;
  color: #9ca3af;
}

.strategy-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.strategy-card {
  background: white;
  border: 2px solid #f0f0f0;
  border-radius: var(--radius-lg);
  padding: 1rem 0.85rem;
  text-align: left;
  cursor: pointer;
  transition: all var(--transition-fast);
  font-family: 'Poppins', sans-serif;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 130px;
}
.strategy-card:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
}
.strategy-card.selected {
  border-color: var(--color-accent-purple);
  background: var(--purple-50);
}
.dark-theme .strategy-card {
  background: #212e3e;
  border-color: #2a3a4e;
}
.dark-theme .strategy-card.selected {
  background: #1e1a40;
  border-color: var(--color-accent-purple);
}

.strategy-icon-wrap {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
}
.strategy-icon-wrap.purple {
  background: var(--purple-50);
  color: var(--color-accent-purple);
}
.strategy-icon-wrap.blue {
  background: #eef4ff;
  color: #3b82f6;
}
.strategy-icon-wrap.teal {
  background: var(--teal-50);
  color: var(--color-accent-teal);
}
.strategy-icon-wrap.coral {
  background: #fdeee7;
  color: #d85a30;
}
.dark-theme .strategy-icon-wrap.purple {
  background: #2a2060;
}
.dark-theme .strategy-icon-wrap.blue {
  background: #0d1a30;
}
.dark-theme .strategy-icon-wrap.teal {
  background: #0d2420;
}
.dark-theme .strategy-icon-wrap.coral {
  background: #2e1d18;
}

.strategy-name {
  font-size: 0.85rem;
  font-weight: 700;
  color: #213547;
  margin: 0;
}
.strategy-card.selected .strategy-name {
  color: var(--color-accent-purple);
}
.dark-theme .strategy-name {
  color: white;
}
.strategy-desc {
  font-size: 0.7rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.35;
}
.dark-theme .strategy-desc {
  color: #9aa6b8;
}

.strategy-stats {
  display: flex;
  gap: 8px;
  margin-top: auto;
  padding-top: 6px;
  flex-wrap: wrap;
}
.strategy-stat {
  font-size: 0.65rem;
  font-weight: 700;
  color: var(--color-primary);
  background: #f4f4f6;
  padding: 2px 6px;
  border-radius: 4px;
}
.strategy-stat-warn {
  background: #fdeee7;
  color: #b03d12;
}
.dark-theme .strategy-stat {
  background: #1a2535;
  color: white;
}
.dark-theme .strategy-stat-warn {
  background: #2e1d18;
  color: #f5b89a;
}

.custom-order-card {
  padding: 1rem !important;
}
.custom-order-title {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--color-primary);
  margin: 0 0 4px;
}
.dark-theme .custom-order-title {
  color: white;
}
.custom-order-sub {
  font-size: 0.72rem;
  color: #6b7280;
  margin: 0 0 0.85rem;
}
.dark-theme .custom-order-sub {
  color: #9aa6b8;
}
.custom-order-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0.55rem 0.6rem;
  background: #fafbfc;
  border-radius: var(--radius-sm);
  margin-bottom: 6px;
}
.dark-theme .custom-order-row {
  background: #1a2535;
}
.custom-order-num {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--color-accent-purple);
  color: white;
  font-size: 0.7rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.custom-order-name {
  flex: 1;
  font-size: 0.82rem;
  font-weight: 600;
  color: #213547;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dark-theme .custom-order-name {
  color: white;
}
.custom-order-bal {
  font-size: 0.75rem;
  color: #6b7280;
  flex-shrink: 0;
}
.dark-theme .custom-order-bal {
  color: #9aa6b8;
}
.reorder-buttons {
  display: flex;
  gap: 2px;
  flex-shrink: 0;
}

.results-card {
  background: linear-gradient(135deg, var(--purple-50) 0%, var(--teal-50) 100%);
  border-radius: var(--radius-lg);
  padding: 1.25rem 1.15rem;
  border: 1px solid #ece9fb;
  animation: cardSlideUp 0.4s cubic-bezier(0.34, 1.2, 0.64, 1) both;
}
.dark-theme .results-card {
  background: linear-gradient(135deg, #1e1a40 0%, #0d2420 100%);
  border-color: #2a3a4e;
}
.result-stat-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.85rem;
}

.unpayable-warning {
  display: flex;
  gap: 12px;
  padding: 1rem 1.15rem;
  background: #fdeee7;
  border: 1px solid #f5cdb8;
  border-radius: var(--radius-lg);
  align-items: flex-start;
}
.dark-theme .unpayable-warning {
  background: #2e1d18;
  border-color: #4a2a20;
}
.unpayable-icon {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-sm);
  background: #d85a30;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
}
.unpayable-text {
  flex: 1;
}
.unpayable-title {
  font-size: 0.88rem;
  font-weight: 700;
  color: #b03d12;
  margin: 0 0 4px;
}
.dark-theme .unpayable-title {
  color: #f5b89a;
}
.unpayable-body {
  font-size: 0.78rem;
  color: #4b5563;
  margin: 0;
  line-height: 1.45;
}
.dark-theme .unpayable-body {
  color: #cbd5e1;
}

@media (min-width: 480px) {
  .result-stat-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}
.result-stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.result-stat-label {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #6b7280;
}
.dark-theme .result-stat-label {
  color: #9aa6b8;
}
.result-stat-value {
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--color-primary);
  letter-spacing: -0.02em;
}
.dark-theme .result-stat-value {
  color: white;
}
.result-stat-savings .result-stat-value {
  color: var(--color-accent-green);
}
.dark-theme .result-stat-savings .result-stat-value {
  color: var(--color-accent-teal);
}
.result-stat-sub {
  font-size: 0.7rem;
  color: #6b7280;
}
.dark-theme .result-stat-sub {
  color: #9aa6b8;
}

.milestones-card {
  padding: 0 !important;
  overflow: hidden;
}
.expandable-header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.15rem;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  text-align: left;
}
.expandable-title {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--color-primary);
}
.dark-theme .expandable-title {
  color: white;
}
.expandable-chevron {
  font-size: 12px;
  color: #6b7280;
  transition: transform var(--transition-fast);
}
.expandable-chevron.open {
  transform: rotate(180deg);
}

.milestones-body {
  padding: 0 1.15rem 1rem;
}
.milestone-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 0.65rem 0;
  border-top: 1px solid #f4f4f6;
}
.dark-theme .milestone-row {
  border-top-color: #2a3a4e;
}
.milestone-info {
  flex: 1;
  min-width: 0;
}
.milestone-name {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  color: #213547;
  margin-bottom: 2px;
}
.dark-theme .milestone-name {
  color: white;
}
.milestone-meta {
  display: block;
  font-size: 0.7rem;
  color: #6b7280;
}
.dark-theme .milestone-meta {
  color: #9aa6b8;
}
.milestone-date {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--color-accent-purple);
  flex-shrink: 0;
}
.milestone-date-unpaid {
  color: #d85a30;
}

.schedule-body {
  padding: 0 1.15rem 1rem;
}
.schedule-table-wrap {
  max-height: 400px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid #f0f0f0;
  border-radius: var(--radius-sm);
}
.dark-theme .schedule-table-wrap {
  border-color: #2a3a4e;
}
.schedule-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem;
}
.schedule-table th {
  position: sticky;
  top: 0;
  background: #f8f8fb;
  text-align: left;
  padding: 0.55rem 0.7rem;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 0.65rem;
  border-bottom: 1px solid #e7e7e7;
}
.dark-theme .schedule-table th {
  background: #1a2535;
  color: #9aa6b8;
  border-bottom-color: #2a3a4e;
}
.schedule-table td {
  padding: 0.5rem 0.7rem;
  border-bottom: 1px solid #f4f4f6;
  color: #213547;
}
.dark-theme .schedule-table td {
  color: #d4d2d2;
  border-bottom-color: #2a3a4e;
}
.schedule-table tr:last-child td {
  border-bottom: none;
}
.text-right {
  text-align: right;
}

.compare-title {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--color-primary);
  margin: 0 0 0.85rem;
}
.dark-theme .compare-title {
  color: white;
}
.compare-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.compare-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.7rem 0.85rem;
  background: #fafbfc;
  border-radius: var(--radius-sm);
  border: 1px solid transparent;
  gap: 12px;
}
.dark-theme .compare-row {
  background: #1a2535;
}
.compare-row.best {
  border-color: var(--color-accent-green);
  background: linear-gradient(135deg, #e8f5ee 0%, #fafbfc 100%);
}
.dark-theme .compare-row.best {
  border-color: var(--color-accent-teal);
  background: linear-gradient(135deg, #0f2a1c 0%, #1a2535 100%);
}
.compare-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: #213547;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}
.dark-theme .compare-name {
  color: white;
}
.best-tag {
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  background: var(--color-accent-green);
  color: white;
  padding: 2px 6px;
  border-radius: 999px;
}
.dark-theme .best-tag {
  background: var(--color-accent-teal);
  color: var(--color-primary);
}
.compare-stats {
  display: flex;
  gap: 12px;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--color-primary);
  flex-shrink: 0;
}
.dark-theme .compare-stats {
  color: white;
}
.compare-stat-warn {
  color: #d85a30;
}
.dark-theme .compare-stat-warn {
  color: #f5b89a;
}
</style>
